import type { NextPage } from 'next';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';

import { Badge, Grid, RadioButton, Typography } from '@sravni/react-design-system';
import type { RadioButtonProps } from '@sravni/react-design-system/lib/RadioButton';
import { useIsMobile } from '@sravni/react-utils';

import type { Application } from '@src/@types/app';
import BottomBannerContainer from '@src/components/BottomBannerContainer';
import { LogoBox } from '@src/components/LogoBox';
import { cards } from '@src/components/promo/hardcode';
import { mfoIcons } from '@src/components/promo/icons';
import { PromoSection } from '@src/components/promo/PromoSection';
import type { CardProps } from '@src/components/promo/PromoSection/Card';
import { getSidebarBannerData } from '@src/config/getSidebarBannerData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { PageContext } from '@src/config/PageContext';
import { ListKeys } from '@src/constants/microcredits';
import { useBestProducts } from '@src/hooks/useBestProducts';
import { useListPage } from '@src/hooks/useListPage';
import { Layout } from 'app/Layout';
import { SeoComponents } from 'widgets/SeoComponents';

import styles from './styles.module.scss';

const { Heading, Text } = Typography;

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.PROMOTION,
  source: 'search',
  seoLinksVisible: true,
  withMonetizationCardLogoClick: true,
};

const options: RadioButtonProps['options'] = [
  {
    label: 'Акции',
    value: 'promotion',
  },
  {
    label: 'Промокоды',
    value: 'promoCodes',
  },
];

enum CardsColSize {
  Mobile = 12,
  Desktop = 4,
}

const promoCodeCards: Array<CardProps & { promoCode: string }> = [];
const cardArray = [
  {
    headline: 'Акции',
    cards,
  },
  {
    headline: 'Промокоды',
    cards: promoCodeCards,
  },
];

const Promotion: NextPage = () => {
  const { seo, hasUtmLabel } = useListPage(ListKeys.PROMOTION);
  const [sortValue, setSort] = useState(options[0].value);
  const isMobile = useIsMobile();

  const colSize = isMobile ? CardsColSize.Mobile : CardsColSize.Desktop;

  useBestProducts();

  return (
    <Layout>
      <div className="slimContent">
        <PageContext.Provider value={pageConfig}>
          <div className={styles.container}>
            <div className={styles.content}>
              <div className={styles.headlineContainer}>
                <Heading level={1} className={styles.headline}>
                  Акции, {isMobile ? <br /> : ''} промокоды,
                  <br />
                  скидки от МФО
                </Heading>
                <Badge color="violet" variant="primary" text="С нами выгоднее" className={styles.badge} />
                <Text className="h-color-D60">Выбирайте займы выгодно</Text>
              </div>
              <LogoBox items={[...Object.values(mfoIcons), ...Object.values(mfoIcons)]} />
              {cardArray.every(({ cards }) => cards.length) && (
                <div className="h-pb-4 h-color-D10">
                  <RadioButton options={options} value={sortValue} onChange={setSort} className={styles.radio} />
                </div>
              )}
              {(sortValue === 'promotion' ? cardArray : cardArray.slice().reverse()).map(
                ({ headline, cards }) =>
                  !!cards.length && (
                    <PromoSection headline={headline} key={headline}>
                      <Grid.Row gutter={[20, 20]}>
                        {cards.map((card) => (
                          <Grid.Col key={card.text} span={colSize}>
                            <PromoSection.Card {...card} key={card.organization} />
                          </Grid.Col>
                        ))}
                      </Grid.Row>
                    </PromoSection>
                  ),
              )}
            </div>
          </div>
          {!hasUtmLabel && seo && <SeoComponents seo={seo} breadcrumbs={seo.breadcrumbs} prefooter={seo.prefooter} />}
          <BottomBannerContainer />
        </PageContext.Provider>
      </div>
    </Layout>
  );
};

Promotion.getInitialProps = async (context: Application.ReduxNextPageContext) => {
  await Promise.all([getSidebarBannerData(context)]);
};

export default Promotion;
